// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Plus, Edit2, Check, X, Trash2, Upload, Send, Loader2 } from "lucide-react";
import { usePositionsBySport } from "@/hooks/usePositionsBySport";
import { sendRosterInvitation } from '@/utils/rosterInvitations';
import RosterCSVImport from './RosterCSVImport';
import { useAuth } from "@/contexts/AuthContext";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

interface RosterMember {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  cell_phone?: string;
  jersey_number?: string;
  position?: string;
  grad_year?: string;
  profile_id?: string;
  invite_sent: boolean;
  invite_token?: string;
  profiles?: {
    status: string;
    user_id: string | null;
  };
}

interface TeamRosterSectionProps {
  teamId: string;
  onRosterUpdated: () => void;
}

interface Team {
  id: string;
  sport_id: string;
  name: string;
  head_coach_email?: string;
}

const TeamRosterSection: React.FC<TeamRosterSectionProps> = ({ teamId, onRosterUpdated }) => {
  const { profile } = useAuth();
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState<Team | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ jersey_number: string; position: string }>({
    jersey_number: '',
    position: ''
  });
  
  // Full edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<RosterMember | null>(null);
  const [fullEditValues, setFullEditValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    cell_phone: '',
    grad_year: '',
    jersey_number: '',
    position: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Reinvite state
  const [isReinviting, setIsReinviting] = useState(false);

  // Get positions filtered by team's sport
  const { positions } = usePositionsBySport(team?.sport_id || null);

  // Graduation years dropdown options
  const gradYears = [2025, 2026, 2027, 2028, 2029, 2030, 2031, 2032, 2033, 2034, 2035];
  
  const [newAthlete, setNewAthlete] = useState({
    first_name: '',
    last_name: '',
    email: '',
    cell_phone: '',
    grad_year: '',
    jersey_number: '',
    position: ''
  });

  useEffect(() => {
    fetchTeamData();
    fetchRoster();
  }, [teamId]);

  const fetchTeamData = async () => {
    try {
      const { data, error } = await supabase
        .from('coach_teams')
        .select('id, sport_id, name, head_coach_email')
        .eq('id', teamId)
        .single();

      if (error) throw error;
      setTeam(data);
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  const fetchRoster = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('coach_team_rosters')
        .select(`
          *,
          profiles:profile_id (
            status,
            user_id,
            username
          )
        `)
        .eq('team_id', teamId)
        .order('last_name');

      if (error) throw error;
      console.log('🚨 DEBUG: Team roster data with profiles:', data);
      setRoster(data || []);
    } catch (error) {
      console.error('Error fetching roster:', error);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (member: RosterMember) => {
    setEditingId(member.id);
    setEditValues({
      jersey_number: member.jersey_number || '',
      position: member.position || ''
    });
  };

  const handleSaveEdit = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('coach_team_rosters')
        .update({
          jersey_number: editValues.jersey_number || null,
          position: editValues.position || null
        })
        .eq('id', memberId);

      if (error) throw error;

      setEditingId(null);
      fetchRoster();
      toast.success('Roster updated');
    } catch (error) {
      console.error('Error updating roster:', error);
      toast.error('Failed to update roster');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ jersey_number: '', position: '' });
  };
  
  // Full edit modal handlers
  const handleOpenFullEdit = (member: RosterMember) => {
    setEditingMember(member);
    setFullEditValues({
      first_name: member.first_name || '',
      last_name: member.last_name || '',
      email: member.email || '',
      cell_phone: member.cell_phone || '',
      grad_year: member.grad_year || '',
      jersey_number: member.jersey_number || '',
      position: member.position || ''
    });
    setEditModalOpen(true);
  };
  
  const handleSaveFullEdit = async () => {
    if (!editingMember) return;
    
    setSavingEdit(true);
    try {
      const { error } = await supabase
        .from('coach_team_rosters')
        .update({
          first_name: fullEditValues.first_name.trim() || null,
          last_name: fullEditValues.last_name.trim() || null,
          email: fullEditValues.email.trim() || null,
          cell_phone: fullEditValues.cell_phone || null,
          grad_year: fullEditValues.grad_year || null,
          jersey_number: fullEditValues.jersey_number || null,
          position: fullEditValues.position || null
        })
        .eq('id', editingMember.id);

      if (error) throw error;

      setEditModalOpen(false);
      setEditingMember(null);
      fetchRoster();
      onRosterUpdated();
      toast.success('Athlete updated successfully');
    } catch (error) {
      console.error('Error updating athlete:', error);
      toast.error('Failed to update athlete');
    } finally {
      setSavingEdit(false);
    }
  };
  
  // Reinvite all pending athletes
  const handleReinviteAllPending = async () => {
    if (!team || !profile) return;
    
    const pendingMembers = roster.filter(m => !m.profile_id && m.email);
    
    if (pendingMembers.length === 0) {
      toast.info('No pending athletes with email addresses to invite');
      return;
    }
    
    setIsReinviting(true);
    try {
      const rosterIds = pendingMembers.map(m => m.id);
      
      const { data, error } = await invokeEdgeFunction('resend-roster-invitations', {
        body: {
          roster_ids: rosterIds,
        }
      });

      if (error) throw error;

      toast.success(`Sent ${data.emails_sent || 0} invitation emails to pending athletes`);
      fetchRoster();
    } catch (error) {
      console.error('Error reinviting pending athletes:', error);
      toast.error('Failed to send invitations');
    } finally {
      setIsReinviting(false);
    }
  };

  const handleDeleteAthlete = async (member: RosterMember) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to remove ${member.first_name} ${member.last_name} from the roster?`
    );
    
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('coach_team_rosters')
        .delete()
        .eq('id', member.id);

      if (error) throw error;

      fetchRoster();
      onRosterUpdated();
      toast.success('Athlete removed from roster');
    } catch (error) {
      console.error('Error removing athlete:', error);
      toast.error('Failed to remove athlete');
    }
  };

  const handleAddAthlete = async () => {
    if (!newAthlete.first_name.trim() || !newAthlete.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }

    try {
      // Check if email matches existing athlete
      let profileId = null;
      let hasExistingProfile = false;
      
      if (newAthlete.email) {
        const cleanEmail = newAthlete.email.trim().toLowerCase();
        console.log("Email to check:", cleanEmail);
        console.log("Current user:", (await getSessionUser()).data.user);
        
        const { data: existingProfile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', cleanEmail)
          .maybeSingle();

        console.log("Profile check result:", { existingProfile, profileError });

        if (existingProfile) {
          profileId = existingProfile.id;
          hasExistingProfile = true;
          console.log("Existing profile found:", profileId);
        } else {
          console.log("No existing profile found for:", newAthlete.email);
        }
      }

      const rosterData = {
        team_id: teamId,
        first_name: newAthlete.first_name.trim(),
        last_name: newAthlete.last_name.trim(),
        email: newAthlete.email || null,
        cell_phone: newAthlete.cell_phone || null,
        grad_year: newAthlete.grad_year || null,
        jersey_number: newAthlete.jersey_number || null,
        position: newAthlete.position || null,
        profile_id: profileId,
        invite_sent: false
      };

      const { data: insertedRoster, error } = await supabase
        .from('coach_team_rosters')
        .insert(rosterData)
        .select()
        .single();

      if (error) throw error;

      // Send multi-channel invitation (email + SMS + notification) if athlete has email
      if (newAthlete.email && team) {
        try {
          console.log("🚀 Sending multi-channel invitation for:", newAthlete.email);
          
          // Get current user info
          const currentUser = (await getSessionUser()).data.user;
          const { data: coachProfile } = await supabase
            .from('profiles')
            .select('name')
            .eq('user_id', currentUser?.id)
            .single();

          // Use the comprehensive invitation system
          await sendRosterInvitation(
            teamId,
            insertedRoster.id,
            {
              first_name: newAthlete.first_name.trim(),
              last_name: newAthlete.last_name.trim(),
              email: newAthlete.email.trim().toLowerCase(),
              cell_phone: newAthlete.cell_phone || undefined
            },
            team.name,
            coachProfile?.name || 'Coach',
            currentUser?.id || ''
          );

          toast.success(`Multi-channel invitation sent to ${newAthlete.email}!`);
          
        } catch (error) {
          console.error('Invitation sending error:', error);
          toast.error('Athlete added but invitation failed to send: ' + error.message);
        }
      }

      // Reset form
      setNewAthlete({
        first_name: '',
        last_name: '',
        email: '',
        cell_phone: '',
        grad_year: '',
        jersey_number: '',
        position: ''
      });

      fetchRoster();
      onRosterUpdated();
      toast.success('Athlete added to roster');

      if (profileId) {
        toast.success('Linked to existing athlete profile');
      } else if (newAthlete.email && !hasExistingProfile) {
        toast.success('Profile setup email sent to athlete');
      }
    } catch (error) {
      console.error('Error adding athlete:', error);
      toast.error('Failed to add athlete');
    }
  };

  const getStatusBadge = (member: RosterMember) => {
    // Check if athlete has a confirmed profile (both user_id and profile are set)
    if (member.profiles && member.profiles.user_id && member.profiles.status === 'active') {
      return <Badge variant="success">Confirmed</Badge>;
    } else if (member.profile_id && member.profiles && member.profiles.status === 'pending_setup') {
      return <Badge variant="premium">Pending Setup</Badge>;
    } else if (member.invite_sent) {
      return <Badge variant="secondary">Pending</Badge>;
    } else {
      return <Badge variant="outline">Not Invited</Badge>;
    }
  }

  if (loading) {
    return <div className="text-center py-4">Loading roster...</div>;
  }

  return (
    <Tabs defaultValue="manual" className="space-y-4">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="manual" className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Manual Entry
        </TabsTrigger>
        <TabsTrigger value="import" className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          CSV Import
        </TabsTrigger>
      </TabsList>

      <TabsContent value="manual">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Team Roster ({roster.length})
            </CardTitle>
            {roster.filter(m => !m.profile_id && m.email).length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReinviteAllPending}
                disabled={isReinviting}
              >
                {isReinviting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Reinvite All Pending ({roster.filter(m => !m.profile_id && m.email).length})
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Roster Table */}
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Athlete Name</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Jersey #</TableHead>
                    <TableHead>Cell Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roster.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {member.first_name} {member.last_name}
                          </div>
                          {member.email && (
                            <div className="text-sm text-gray-500">{member.email}</div>
                          )}
                          {member.grad_year && (
                            <div className="text-sm text-gray-500">Class of {member.grad_year}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {editingId === member.id ? (
                          <Select
                            value={editValues.position}
                            onValueChange={(value) => setEditValues(prev => ({ ...prev, position: value }))}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue placeholder="Position" />
                            </SelectTrigger>
                            <SelectContent>
                              {positions.map((pos) => (
                                <SelectItem key={pos.id} value={pos.position_name}>
                                  {pos.position_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          member.position || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === member.id ? (
                          <Input
                            value={editValues.jersey_number}
                            onChange={(e) => setEditValues(prev => ({ ...prev, jersey_number: e.target.value }))}
                            placeholder="#"
                            className="w-16"
                          />
                        ) : (
                          member.jersey_number || '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {member.cell_phone || '-'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(member)}
                      </TableCell>
                       <TableCell>
                         {editingId === member.id ? (
                           <div className="flex gap-1">
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => handleSaveEdit(member.id)}
                             >
                               <Check className="h-3 w-3" />
                             </Button>
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={handleCancelEdit}
                             >
                               <X className="h-3 w-3" />
                             </Button>
                           </div>
                         ) : (
                           <div className="flex gap-1">
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => handleOpenFullEdit(member)}
                               title="Edit all fields"
                             >
                               <Edit2 className="h-3 w-3" />
                             </Button>
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => handleDeleteAthlete(member)}
                               className="text-destructive hover:text-destructive"
                             >
                               <Trash2 className="h-3 w-3" />
                             </Button>
                           </div>
                         )}
                       </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Add New Athlete Row */}
                  <TableRow className="bg-gray-50">
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            placeholder="First Name"
                            value={newAthlete.first_name}
                            onChange={(e) => setNewAthlete(prev => ({ ...prev, first_name: e.target.value }))}
                            className="flex-1"
                          />
                          <Input
                            placeholder="Last Name"
                            value={newAthlete.last_name}
                            onChange={(e) => setNewAthlete(prev => ({ ...prev, last_name: e.target.value }))}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="email"
                            placeholder="Email"
                            value={newAthlete.email}
                            onChange={(e) => setNewAthlete(prev => ({ ...prev, email: e.target.value }))}
                            className="flex-1"
                          />
                           <Select
                             value={newAthlete.grad_year}
                             onValueChange={(value) => setNewAthlete(prev => ({ ...prev, grad_year: value }))}
                           >
                             <SelectTrigger className="w-28">
                               <SelectValue placeholder="Grad Year" />
                             </SelectTrigger>
                             <SelectContent>
                               {gradYears.map((year) => (
                                 <SelectItem key={year} value={year.toString()}>
                                   {year}
                                 </SelectItem>
                               ))}
                             </SelectContent>
                           </Select>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={newAthlete.position}
                        onValueChange={(value) => setNewAthlete(prev => ({ ...prev, position: value }))}
                        disabled={!team?.sport_id || positions.length === 0}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder={!team?.sport_id ? "Select sport first" : "Position"} />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.length > 0 ? (
                            positions.map((pos) => (
                              <SelectItem key={pos.id} value={pos.position_name}>
                                {pos.position_name}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              Edit team to select a sport
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="#"
                        value={newAthlete.jersey_number}
                        onChange={(e) => setNewAthlete(prev => ({ ...prev, jersey_number: e.target.value }))}
                        className="w-16"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="xxx-xxx-xxxx"
                        value={newAthlete.cell_phone}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Remove all non-digit characters
                          const digits = value.replace(/\D/g, '');
                          // Format as xxx-xxx-xxxx
                          let formatted = '';
                          if (digits.length > 0) {
                            formatted = digits.substring(0, 3);
                            if (digits.length > 3) {
                              formatted += '-' + digits.substring(3, 6);
                              if (digits.length > 6) {
                                formatted += '-' + digits.substring(6, 10);
                              }
                            }
                          }
                          setNewAthlete(prev => ({ ...prev, cell_phone: formatted }));
                        }}
                        className="w-32"
                        maxLength={12}
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">New</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={handleAddAthlete}
                        className="flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {roster.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No athletes on this team yet.</p>
                <p className="text-sm">Use the form above to add your first athlete or import from CSV.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="import">
        {team && (
          <RosterCSVImport
            teamId={teamId}
            teamName={team.name}
            onImportComplete={() => {
              fetchRoster();
              onRosterUpdated();
            }}
          />
        )}
      </TabsContent>

      {/* Full Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Athlete</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-first-name">First Name</Label>
                <Input
                  id="edit-first-name"
                  value={fullEditValues.first_name}
                  onChange={(e) => setFullEditValues(prev => ({ ...prev, first_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Last Name</Label>
                <Input
                  id="edit-last-name"
                  value={fullEditValues.last_name}
                  onChange={(e) => setFullEditValues(prev => ({ ...prev, last_name: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={fullEditValues.email}
                onChange={(e) => setFullEditValues(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Cell Phone</Label>
              <Input
                id="edit-phone"
                value={fullEditValues.cell_phone}
                onChange={(e) => setFullEditValues(prev => ({ ...prev, cell_phone: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-grad-year">Grad Year</Label>
                <Select
                  value={fullEditValues.grad_year}
                  onValueChange={(value) => setFullEditValues(prev => ({ ...prev, grad_year: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {gradYears.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-jersey">Jersey #</Label>
                <Input
                  id="edit-jersey"
                  value={fullEditValues.jersey_number}
                  onChange={(e) => setFullEditValues(prev => ({ ...prev, jersey_number: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-position">Position</Label>
                <Select
                  value={fullEditValues.position}
                  onValueChange={(value) => setFullEditValues(prev => ({ ...prev, position: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Position" />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((pos) => (
                      <SelectItem key={pos.id} value={pos.position_name}>
                        {pos.position_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFullEdit} disabled={savingEdit}>
              {savingEdit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tabs>
  );
};

export default TeamRosterSection;